import { Box, Flex, Grid, IconButton, Image, Text, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, FormControl, FormLabel, Input, Button, Select, FormErrorMessage, Modal, ModalOverlay, ModalCloseButton, ModalContent, ModalBody, PinInput, PinInputField, useDisclosure } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import MyDiv from './bankDetails.style';
import { BankDetail, BankDetailHeaders, BOption, IBankDetail, props, BankSchema } from './modal';
import BankDetailLogo from '../../../../../assets/images/BankDetailIcon.svg';
import EditLogo from '../../../../../assets/images/EditLogo.svg';
import { AlertCard, CustomToast } from '../../../../../components';
import { useFetch } from '../../../../../hooks/useFetch';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from '../../../../../hooks/useAuth';

const BankDetails = ({ employeeId }: props) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [bankMembers, setBankMembers] = useState<BankDetail>();
  const [bankOptions, setBankOption] = useState<BOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [maskedAccount, setMaskedAccount] = useState("");
  const { hasPermission, loginObj } = useAuth();
  const loggedInEmpId = loginObj?.employeeId;
  const canViewOthers = hasPermission("Employee", "showCriticalField");
  const isOwnProfile = loggedInEmpId === employeeId;
  const [alertConfig, setAlertConfig] = useState<{ description: string; colorScheme: string; onConfirm: () => void; } | null>(null);
  const [sendOtp, setSendOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpbox, setOtpbox] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const canEditBank = hasPermission("Employee", "UpdateOwnBankDetail") && hasPermission("Employee", "showCriticalField");

  const { control, handleSubmit, formState: { errors }, reset } = useForm<IBankDetail>({
    resolver: yupResolver(BankSchema),
    defaultValues: {
      account_Holder_Name: "",
      account_Number: "",
      check_account_Number: "",
      ifsc_Code: "",
      bank_Name: 0,
      branch_Name: "",
    },
  });

  async function loadBankMembers() {
    const response = await fetchApi("Hrlense_Employee/bankAccountDetail", "GET", null, `employeeId=${employeeId}`, "");
    if (response) {
      setBankMembers(response);
    } else {
      setBankMembers({
        id: 0,
        bank_Name: 0,
        account_Number: "",
        ifsc_Code: "",
        branch_Name: "",
      } as BankDetail);
    }
  }

  const loadBankOptions = async () => {
    const response = await fetchApi("DropDown/bankName", "GET", null, "", "");
    if (response) setBankOption(response);
  };

  useEffect(() => {
    if (employeeId) {
      loadBankMembers();
      loadBankOptions();
    }
  }, [employeeId]);

  const handleEditClick = (member: BankDetail) => {
    setSelectedId(member.id ?? null);
    reset({
      account_Holder_Name: member.account_Holder_Name,
      bank_Name: member.bank_Name,
      account_Number: member.account_Number,
      check_account_Number: "",
      ifsc_Code: member.ifsc_Code,
      branch_Name: member.branch_Name,
    });
    setMaskedAccount(maskAccountNumber(member.account_Number ?? ""));
    setDrawerOpen(true);
  };

  const onSubmit = async (formData: IBankDetail) => {
    const payload = {
      id: selectedId ?? 0,
      account_Holder_Name: formData?.account_Holder_Name,
      employeeId: employeeId ?? 0,
      bank_Name: formData.bank_Name,
      account_Number: formData.account_Number,
      branch_Name: formData.branch_Name,
      ifsc_Code: formData.ifsc_Code,
    };

    const response = await fetchApi(`Hrlense_Employee/bankAccountDetail`, "POST", payload, null, "Bank detail updated successfully");
    if (response) {
      setDrawerOpen(false);
      loadBankMembers();
    }
  };


  const maskAccountNumber = (digits: string) => {
    const len = digits.length;
    if (len <= 4) {
      return "X".repeat(len);        // Show X, XX, XXX, XXXX
    }
    const masked = "X".repeat(len - 4);
    const last4 = digits.slice(-4);
    const groupedMasked = masked.replace(/(.{4})/g, "$1 ").trim();
    return `${groupedMasked} ${last4}`;
  };

  const sendOtpforIdentity = async () => {
    setSendOtp(true);
    const res = await fetchApi("Tds_Slab/otpforsecure", "POST",
      { employee_Id: employeeId, purpose: "Identity Details" },
      null, "");
    if (res) {
      setSendOtp(false); setOtpbox(true);
      addToast({ message: res.message, status: "success" });
    }
    if (!res) { setSendOtp(false); onClose(); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
      addToast({ message: "Enter 6 digit Verification Code", status: "error" });
      return;
    }
    setVerifying(true);
    const res = await fetchApi("Tds_Slab/verifyotp", "POST", { purpose: "Identity Details", otp });
    if (res) { setVerifying(false); setOtp(""); setOtpbox(false); setOtpVerified(true); addToast({ message: res.message, status: "success" }); }
    if (!res) {
      setVerifying(false); setOtp("");
    }
  };
  const getDisplayValue = (key: string, value?: string) => {
    if (!value) return "-";
    if (canViewOthers) return value;
    if (isOwnProfile && otpVerified) return value;
    if (isOwnProfile) {
      if (key === "account_Number") {
        return maskAccountNumber(value);
      }
      if (key === "ifsc_Code") {
        return value.slice(0, 4) + "XXXX";
      }
      return value;
    }
    return "********";
  };


  return (
    <MyDiv>
      <Box pt={2} mb={6} className="main">
        <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
          <Flex align="center" gap={3}>
            <Image src={BankDetailLogo} alt="Bank Detail Icon" />
            <Text className="font-poppins text_sm text_medium col">{`Bank Detail `}</Text>
          </Flex>

          {!canViewOthers && isOwnProfile && !otpVerified && (
            <Button
              size="xs"
              variant="link"
              colorScheme="blue"
              onClick={() =>
                setAlertConfig({
                  description: "For security purposes, a verification code is required to access full details. Proceed to receive the code?",
                  colorScheme: "green",

                  onConfirm: () => sendOtpforIdentity(),
                })
              }
            >
              View
            </Button>
          )}

          {(hasPermission('Employee', 'UpdateEmpoyeesBankDetail') || (canEditBank && isOwnProfile)) && (<IconButton aria-label="Edit" icon={<Image src={EditLogo} />} variant="none" onClick={() => handleEditClick(bankMembers as BankDetail)} />)}
        </Flex>
        {BankDetailHeaders?.map((data, j) => {
          const rawValue: any = bankMembers?.[data.key];
          const displayValue = getDisplayValue(data.key, rawValue);

          return (
            <Box key={j} py={2} px={10} bg="#FFFFFF" borderRadius={8}>
              <Grid templateColumns="1fr 1fr" alignItems="center" mb={2}>
                <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)"  >{data.label}</Text>
                <Text className="font-poppins text_sm text_medium">{displayValue}</Text>
              </Grid>
            </Box>
          );
        })}

      </Box>
      <AlertCard
        isLoading={sendOtp} title="Access Confirmation" isOpen={!!alertConfig} onClose={() => setAlertConfig(null)} onConfirm={() => { if (alertConfig?.onConfirm) alertConfig.onConfirm(); setAlertConfig(null); }} description={alertConfig?.description || ''} colorScheme={alertConfig?.colorScheme || 'red'}
      />
      <Modal isOpen={otpbox} onClose={() => setOtpbox(false)} closeOnOverlayClick={false} closeOnEsc={false} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="12px" p={6}>
          <ModalCloseButton />

          <ModalBody textAlign="center">
            <Text fontSize="xl" fontWeight="600" mb={2}> OTP Verification </Text>
            <Text color="gray.500" mb={6}>
              Please enter the 6-digit verification code sent to your registered official email address.
            </Text>

            <Flex justify="center" gap={3} mb={6}>
              <PinInput otp value={otp} onChange={setOtp}>
                {[...Array(6)].map((_, i) => (
                  <PinInputField key={i} />
                ))}
              </PinInput>
            </Flex>
            <Button isDisabled={verifying}
              width="100%"
              colorScheme="green"
              onClick={verifyOtp}
            >
              Verify Code
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>
      {drawerOpen && (
        <Drawer isOpen={drawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="md">
          <DrawerOverlay />
          <form onSubmit={handleSubmit(onSubmit)}>
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader>Edit Bank Detail</DrawerHeader>
              <DrawerBody pb="80px">
                <FormControl mb={4} isInvalid={!!errors.account_Holder_Name}>
                  <FormLabel>Name as Per Bank Account</FormLabel>
                  <Controller
                    name="account_Holder_Name"
                    control={control}
                    render={({ field }) => <Input placeholder="Enter Name" {...field} />}
                  />
                  <FormErrorMessage>{errors.branch_Name?.message}</FormErrorMessage>
                </FormControl>
                <FormControl mb={4} isInvalid={!!errors.account_Number}>
                  <FormLabel>Account Number</FormLabel>
                  <Controller
                    name="account_Number"
                    control={control}
                    render={({ field }) => (
                      <Input placeholder="Enter Account Number" value={maskedAccount} autoComplete="off" maxLength={18}
                        onKeyDown={(e) => {
                          if (!/[0-9]/.test(e.key) && e.key !== "Backspace") {
                            e.preventDefault();
                            return;
                          }
                          // HANDLE BACKSPACE
                          if (e.key === "Backspace") {
                            const newValue = field.value.slice(0, -1);
                            field.onChange(newValue);
                            setMaskedAccount(maskAccountNumber(newValue));
                            e.preventDefault();
                            return;
                          }

                          if ((field.value || "").length >= 18) {
                            e.preventDefault();
                            return; // stops typing
                          }
                          // HANDLE DIGIT
                          const newValue = (field.value || "") + e.key;
                          field.onChange(newValue);
                          setMaskedAccount(maskAccountNumber(newValue));
                          e.preventDefault();  // prevent actual typing (we manually update UI)
                        }}
                      />
                    )}
                  />

                  <FormErrorMessage>{errors.account_Number?.message}</FormErrorMessage>
                </FormControl>

                <FormControl mb={4} isInvalid={!!errors.check_account_Number}>
                  <FormLabel>Re-enter Account Number</FormLabel>
                  <Controller
                    name="check_account_Number"
                    control={control}
                    render={({ field }) => (
                      <Input
                        placeholder="Enter Account Number Again"
                        maxLength={18} onCopy={(e) => e.preventDefault()} onPaste={(e) => e.preventDefault()} autoComplete="off"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                  <FormErrorMessage>{errors.check_account_Number?.message}</FormErrorMessage>
                </FormControl>

                <FormControl mb={4} isInvalid={!!errors.ifsc_Code}>
                  <FormLabel>IFSC Code</FormLabel>
                  <Controller
                    name="ifsc_Code"
                    control={control}
                    render={({ field }) => <Input placeholder="Enter IFSC Code e.g. SBIN0001234" {...field} />}
                  />
                  <FormErrorMessage>{errors.ifsc_Code?.message}</FormErrorMessage>
                </FormControl>

                <FormControl mb={4} isInvalid={!!errors.bank_Name}>
                  <FormLabel>Bank Name</FormLabel>
                  <Controller
                    name="bank_Name"
                    control={control}
                    render={({ field }) => (
                      <Select placeholder="Select Bank Name" {...field}>
                        {bankOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.bank_Name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                  <FormErrorMessage>{errors.bank_Name?.message}</FormErrorMessage>
                </FormControl>

                <FormControl mb={4} isInvalid={!!errors.branch_Name}>
                  <FormLabel>Branch</FormLabel>
                  <Controller
                    name="branch_Name"
                    control={control}
                    render={({ field }) => <Input placeholder="Enter Branch" {...field} />}
                  />
                  <FormErrorMessage>{errors.branch_Name?.message}</FormErrorMessage>
                </FormControl>
              </DrawerBody>

              <Box position="fixed" bottom="24px" right="24px">
                <Button className="btn_theme" type="submit">Update Detail</Button>
              </Box>
            </DrawerContent>
          </form>
        </Drawer>
      )}
    </MyDiv>
  );
};

export default BankDetails;
